import React, { useEffect } from "react"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import { DateTime } from "luxon"
import classNames from "classnames"

import LayoutNoForm from "../../components/Layout/LayoutNoForm"
import SEO, { PageTypes } from "../../components/seo"
import * as styles from "./blog-post.module.scss"
import BlogPostCard from "../../components/BlogCard/BlogPostCard"
import { useBlogPosts } from "../../hooks"
import { useAuthorsEn } from '../../hooks/useAuthorsEn'
import { useAuthorsRu } from '../../hooks/useAuthorsRu'
import { Locale } from "../../types/Locale.enum"
import ContactFormEmail from "../../components/UI/ContactFormEmail/ContactFormEmail"
import ScrollToTopButton from "../../components/UI/ScrollToTopButton/ScrollToTopButton"

export const MutedLocalesContext = React.createContext([])

const BlogPostTemplate = ({ data, location }) => {
	const intl = useIntl()
	const post = data.markdownRemark
	const { author, date, introduction, title, thumbnail, description, formtext, formtitle } = post.frontmatter

	const minutesToRead = Math.round(
		(post.fields?.readingTime?.minutes) || 1
	)

	const authorsByLanguage = {
		en: useAuthorsEn(),
		ru: useAuthorsRu(),
	}

	const dateObject = DateTime.fromISO(date).setLocale(intl.locale)
	const formattedTitleDate = dateObject.toLocaleString(DateTime.DATE_FULL)
	const formattedDisplayingDate = formattedTitleDate

	const authorArray = authorsByLanguage[intl.locale]?.edges.filter(
		({ node }) =>
			(Array.isArray(author) ? author : [author]).includes(node.frontmatter.id)
	)

	const authorInfo = authorArray?.map(({ node }) => ({
		name: node.frontmatter.name,
		position: node.frontmatter.position,
		avatar: node.frontmatter.avatar.childImageSharp.fixed.src,
	}))

	const blogPosts = useBlogPosts(intl.locale)
	const postsLimit = 3
	const currentSlug = post.fields.slug

	// фильтруем текущий пост и ограничиваем количество
	const blogPostsDisplaying = blogPosts.edges
		.filter(({ node }) => node.fields.slug !== currentSlug)
		.slice(0, postsLimit)

	useEffect(() => {
		const currentPost = blogPosts.edges.find(
			({ node }) => node.fields.slug === window.location.pathname
		)
		if (currentPost?.node.frontmatter.externalLink) {
			window.location.replace(currentPost.node.frontmatter.externalLink)
		}
	}, [blogPosts.edges])

	const Author = authorInfo ? (
		<section className={styles.authorBlock} />
	) : null

	const Introduction = introduction ? (
		<p className={styles.introduction}>{introduction}</p>
	) : null

	const bannerContainerClasses = classNames(styles.bannerContainer, "container")
	const contentClasses = classNames(styles.content)
	const blogWrapperClasses = classNames(styles.blogWrapper)
	const blogItemClasses = classNames(styles.blogItem)
	const blogPostSectionClasses = classNames(styles.blogPostSection, "container")

	const schemaOrgData = {
		date,
		author: authorInfo?.map(a => ({
			name: a.name,
			jobTitle: a.position,
		})),
	}

	return (
		<MutedLocalesContext.Provider value={[Locale.AR]}>
			<LayoutNoForm location={location}>
				<SEO
					language={intl.locale}
					title={title}
					description={description || post.excerpt}
					pathname={location.pathname}
					pageType={PageTypes.BLOG_POST}
					schemaOrgData={schemaOrgData}
				/>
				<article className={styles.article}>
					<header>
						<div className={bannerContainerClasses}>
							<div className={styles.titleBlock}>
								<h1 className={styles.title}>{title}</h1>
								<div className={styles.postInfo}>
									<time className={styles.date} dateTime={date} title={formattedTitleDate}>
										{formattedDisplayingDate}
									</time>
									<span className={styles.timeToRead}> – {minutesToRead} {intl.formatMessage({ id: "blog.post-reading-time" })}</span>
								</div>
							</div>
							{thumbnail?.childImageSharp?.fixed?.src && (
								<div
									className={styles.banner}
									style={{ backgroundImage: `url(${thumbnail.childImageSharp.fixed.src})` }}
								/>
							)}
						</div>
					</header>

					<section className={contentClasses}>
						{Introduction}
						<div dangerouslySetInnerHTML={{ __html: post.html }} />
						{Author}

					</section>


					{formtitle && (
						<section className={styles.cta}>
							<div className={styles.ctaContainer}>
								<div className={styles.ctaForm}>
									<ContactFormEmail title={Array.isArray(formtitle) ? formtitle.join(", ") : formtitle} text={Array.isArray(formtext) ? formtext.join(", ") : formtext} />
								</div>
								<div className={styles.ctaBg} />
							</div>
						</section>
					)}

					<section className={blogPostSectionClasses}>
						<div className={blogWrapperClasses}>
							<h2 className={styles.blogPopularTitle}>You may also enjoy:</h2>
							<ul className={styles.blogPopularList}>
								{blogPostsDisplaying.map(({ node }) => {
									const { date, title, externalLink, thumbnail } = node.frontmatter
									const { slug } = node.fields
									return (
										<li key={slug} className={blogItemClasses}>
											<BlogPostCard
												date={date}
												title={title}
												externalLink={externalLink}
												slug={slug}
												thumbnail={thumbnail}
											/>
										</li>
									)
								})}
							</ul>
						</div>
					</section>
				</article>
				<ScrollToTopButton />
			</LayoutNoForm>
		</MutedLocalesContext.Provider>
	)
}

export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    site {
      siteMetadata {
        description
        title
      }
    }
    markdownRemark(fields: { slug: { eq: $slug } }) {
      id
      excerpt(pruneLength: 160)
      html
      fields {
        readingTime {
          minutes
        }
        slug
      }
      frontmatter {
        author
        description
        title
        date
        introduction
		  formtitle
		  formtext
        thumbnail {
          childImageSharp {
            fixed(width: 1920) {
              src
              width
              height
            }
          }
        }
      }
    }
  }
`
